import {Box, Button, FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack, TextField} from '@mui/material';
import {useEffect, useState} from 'react';
import {z} from 'zod';
import {CouponCode, CouponCodeInput, CouponStatus} from '../../__generated__/resolvers-types';
import {couponCodeSchema} from '../../util/schema/couponCodeSchema';

const couponCodeFormSchema = z.object({
  id: couponCodeSchema,
  name: z.string().min(1, 'Name is required'),
  status: z.nativeEnum(CouponStatus).optional(),
  maxUses: z.number().min(0).optional(),
  expiryDays: z.number().min(1).max(365),
  durationDays: z.number().min(1).max(365),
  couponRole: z.string().min(1, 'Role is required'),
});

export interface CouponCodeFormProps {
  coupon?: CouponCode | null;
  onSubmit: (input: CouponCodeInput) => Promise<void>;
  onCancel: () => void;
}

export default function CouponCodeForm({coupon, onSubmit, onCancel}: CouponCodeFormProps) {
  const [formData, setFormData] = useState<Partial<CouponCodeInput>>({
    id: '',
    name: '',
    status: CouponStatus.ACTIVE,
    maxUses: undefined,
    expiryDays: 30,
    durationDays: 30,
    couponRole: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (coupon) {
      setFormData({
        id: coupon.id,
        name: coupon.name,
        status: coupon.status,
        maxUses: coupon.maxUses || undefined,
        expiryDays: Math.ceil((new Date(coupon.expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)),
        durationDays: coupon.durationDays,
        couponRole: coupon.couponRole,
      });
    }
  }, [coupon]);

  const handleInputChange = (field: keyof CouponCodeInput, value: any) => {
    setFormData((prev) => ({...prev, [field]: value}));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({...prev, [field]: ''}));
    }
  };

  const validateForm = (): boolean => {
    try {
      couponCodeFormSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.issues.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData as CouponCodeInput);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{mt: 2}}>
      <Stack spacing={3}>
        <TextField
          label="Coupon Code"
          value={formData.id || ''}
          onChange={(e) => {
            // Only allow uppercase letters, numbers, and dashes
            const couponCode = couponCodeSchema.safeParse(e.target.value.toUpperCase());
            if (couponCode.success) {
              handleInputChange('id', couponCode.data);
            }
          }}
          error={!!errors.id}
          helperText={errors.id || 'Uppercase letters, numbers, and dashes only (e.g., WELCOME-2024)'}
          disabled={!!coupon} // Disable editing code once created
          fullWidth
          placeholder="e.g., WELCOME-2024"
          sx={{
            '& .MuiInputBase-input': {
              fontFamily: 'monospace',
              textTransform: 'uppercase',
            },
          }}
        />

        <TextField
          label="Name"
          value={formData.name || ''}
          onChange={(e) => handleInputChange('name', e.target.value)}
          error={!!errors.name}
          helperText={errors.name}
          fullWidth
          required
        />

        <FormControl fullWidth error={!!errors.status}>
          <InputLabel>Status</InputLabel>
          <Select
            value={formData.status || CouponStatus.ACTIVE}
            onChange={(e) => handleInputChange('status', e.target.value)}
            label="Status"
          >
            <MenuItem value={CouponStatus.ACTIVE}>Active</MenuItem>
            <MenuItem value={CouponStatus.DISABLED}>Disabled</MenuItem>
          </Select>
          {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
        </FormControl>

        <TextField
          label="Max Uses"
          type="number"
          value={formData.maxUses || ''}
          onChange={(e) => handleInputChange('maxUses', e.target.value ? parseInt(e.target.value, 10) : undefined)}
          error={!!errors.maxUses}
          helperText={errors.maxUses || 'Leave empty for unlimited uses'}
          fullWidth
          inputProps={{min: 0}}
        />

        <TextField
          label="Expiry Days"
          type="number"
          value={formData.expiryDays || ''}
          onChange={(e) => handleInputChange('expiryDays', parseInt(e.target.value, 10))}
          error={!!errors.expiryDays}
          helperText={errors.expiryDays || 'Days from now when the coupon expires (1-365)'}
          fullWidth
          required
          inputProps={{min: 1, max: 365}}
        />

        <TextField
          label="Duration Days"
          type="number"
          value={formData.durationDays || ''}
          onChange={(e) => handleInputChange('durationDays', parseInt(e.target.value, 10))}
          error={!!errors.durationDays}
          helperText={errors.durationDays || 'How long the role grant lasts (1-365 days)'}
          fullWidth
          required
          inputProps={{min: 1, max: 365}}
        />

        <TextField
          label="Role"
          value={formData.couponRole || ''}
          onChange={(e) => handleInputChange('couponRole', e.target.value)}
          error={!!errors.couponRole}
          helperText={errors.couponRole || 'The role this coupon will grant (e.g., "tester", "premium")'}
          fullWidth
          required
        />

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {(() => {
              if (isSubmitting) return 'Saving...';
              if (coupon) return 'Update Coupon';
              return 'Create Coupon';
            })()}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
